import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import networkService from "~/services/network.service";
import Carousel from "~/shared/components/carousel";
import CustomSelect from "~/shared/components/customSelect";
import PageHeader from "~/shared/components/page-header.component";
import { UserRole } from "~/shared/config";
import {
  DEVICE_LICENSE_DETAILS,
  GET_LICENSES_BY_ORG_ID,
  GET_MANUFACTURER,
  ORGANIZATION_PROBES_API,
  PROBE_DETAILS_API,
  REGISTER_DEVICE,
  UPDATE_DEVICE_ON_LICENSE,
} from "~/shared/constants/api";
import { handleError } from "~/shared/utils/errors.util";
import { validateNullishObjectFields } from "~/shared/utils/helper.util";
import useAuthStore from "~/store/auth.store";
import useSidebarStore from "~/store/sidebar.store";
import CustomModal from "~/shared/components/customModal";
import { ChevronLeft, InfoIcon, Maximize } from "lucide-react";
import GenericValidationInput from "../../../../../shared/components/generic-validation-input";
import FullScreenModal from "~/shared/components/full-screen-modal";
import iQ3Image from "/iQ3-serial-number-location.png";
import iQPlusImage from "/iQ+-serial-number-location.png";

interface ProbeData {
  device: {
    id: string;
    externalDeviceId: string;
    nickName: string;
    isActive: boolean;
    modelId: string;
    organization: {
      id: string;
      name: string;
      description: string;
      createdAt: string;
      updatedAt: string;
    };
    registrationStatus: string;
    createdAt: string;
    updatedAt: string;
    organizationId: string;
  };
  owner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const UpdateProbeDetails = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [initialValues, setInitialValues] = useState({
    manufacturer: {
      name: "",
      id: "",
      models: [{ modelName: "", id: "", instructions: "" }],
    },
    model: { modelName: "", id: "" },
    externalDeviceId: "",
    nickName: "",
    planName: "N/A",
    licenseId: "N/A",
  });
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const navigate = useNavigate();
  const { orgId, deviceId } = useParams();
  const [searchParams] = useSearchParams();
  const [collapsed] = useSidebarStore((state) => [state.collapsed]);
  const licenseId = searchParams.get("licenseId");
  const [images, setImages] = useState<any[]>([]);
  const { user } = useAuthStore((state) => ({ user: state.user }));
  const location = useLocation();
  const [selectedModel, setSelectedModel] = useState("iQ3");
  const [referenceImageModel, setReferenceImageModel] = useState("iQ3");
  const [modelIds, setModelIds] = useState({ iQ3: "", iQPlus: "" });
  const [loadingModels, setLoadingModels] = useState(true);
  const [serialNumberError, setSerialNumberError] = useState("");
  const [lookupResult, setLookupResult] = useState<{ exists: boolean; deviceId?: string } | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [showFindItModal, setShowFindItModal] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);

  const validationRules = [
    {
      rule: (value: string) => /^[A-Za-z]{2}/.test(value),
      message: "Must start with 2 letters",
    },
    {
      rule: (value: string) => value.length === 10,
      message: "Contain 10 characters",
    },
    {
      rule: (value: string) => !/[()]/.test(value),
      message: "Doesn't contain parentheses",
    },
  ];

  const getModelFromSerialNumber = (serialNumber: string) => {
    const prefix = serialNumber.substring(0, 2).toUpperCase();
    if (prefix === "BT") {
      return { modelId: modelIds.iQPlus, modelName: "iQ+" };
    } else if (prefix === "KB") {
      return { modelId: modelIds.iQ3, modelName: "iQ3" };
    } else {
      return { modelId: "", modelName: "Probe unknown" };
    }
  };

  const handleValidationChange = (status) => {
    setIsValid(status);
  };

  // Serial number lookup function
  const lookupSerialNumber = useCallback(async (serialNumber: string) => {
    if (!serialNumber || serialNumber.length !== 10) {
      setLookupResult(null);
      return;
    }

    setIsLookingUp(true);
    try {
      const response = await networkService.get<any>(`/devices/${orgId}/sn_lookup/${serialNumber}`);
      // If we get a successful response, the device exists
      setLookupResult({
        exists: true,
        deviceId: response.data?.id || response.data?.deviceId
      });
    } catch (error: any) {
      // If we get a 404 or similar error, the device doesn't exist
      if (error.response?.status === 404 || error.message?.includes('not found')) {
        setLookupResult({ exists: false });
      } else {
        // For other errors, we'll assume it doesn't exist and proceed with registration
        console.error('Lookup error:', error);
        setLookupResult({ exists: false });
      }
    } finally {
      setIsLookingUp(false);
    }
  }, [orgId]);

  const handleFindItClick = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShowFindItModal(true);
  };

  const fetchModelImages = async (modelId: string) => {
    try {
      const galleryResponse = await networkService.get<any>(
        `${GET_MANUFACTURER}/models/${modelId}/gallery`
      );

      const imagePromises = galleryResponse.data.gallery.map(
        async (img: any) => {
          const imageResponse = await networkService.get<Blob>(
            `${GET_MANUFACTURER}/models/${modelId}/gallery/${img.id}`,
            null,
            { responseType: "blob" }
          );
          if (!(imageResponse instanceof Blob)) {
            throw new Error("Response is not a Blob");
          }
          return URL.createObjectURL(imageResponse);
        }
      );
      setImages(await Promise.all(imagePromises));
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
      setImages([]);
    }
  };

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const response = await networkService.get<any>(GET_MANUFACTURER);
        setManufacturers(response.data);
        return response.data;
      } catch (error: any) {
        handleError({ error: error, message: error.message, level: "error" });
      }
    };
    const fetchDevice = async (manufacturers) => {
      try {
        const response = await networkService.get<any>(
          `${REGISTER_DEVICE}/device/${deviceId}`
        );
        const licenseResponse = await networkService.get<any>(
          `${DEVICE_LICENSE_DETAILS}/${deviceId}`
        );
        const data: ProbeData = response.data;
        const licenseData = licenseResponse.data[0];

        const manufacturer = manufacturers.find((man) =>
          man.models.some((model) => model.id === data.device.modelId)
        );
        const model = manufacturer
          ? manufacturer.models.find(
            (model) => model.id === data.device.modelId
          )
          : { modelName: "", id: "" };

        setInitialValues({
          manufacturer: manufacturer
            ? {
              name: manufacturer.name,
              id: manufacturer.id,
              models: manufacturer.models,
            }
            : {
              name: "",
              id: "",
              models: [{ name: "", id: "", instructions: "" }],
            },
          model: model
            ? { modelName: model.modelName, id: model.id }
            : { modelName: "", id: "" },
          externalDeviceId: data.device.externalDeviceId || "",
          nickName: data.device.nickName || "",
          licenseId: licenseData?.id ?? "N/A",
          planName: licenseData?.subscription?.plan?.name ?? "N/A",
        });
        if (manufacturer) {
          setModels(manufacturer.models);
        }
        fetchModelImages(model.id);
      } catch (error: any) {
        handleError({ error: error, message: error.message, level: "error" });
      }
    };
    const fetchLicense = async () => {
      try {
        if (licenseId) {
          const licenseResponse = await networkService.get<any>(
            `${GET_LICENSES_BY_ORG_ID}/${user?.organizationId}`
          );
          const licenseData = licenseResponse.data.results.filter(
            (f) => f.id === licenseId
          )[0];
          setInitialValues((prev) => ({
            ...prev,
            licenseId: licenseData?.id ?? "N/A",
            planName: licenseData?.subscription?.plan?.name ?? "N/A",
          }));
        }
      } catch (error: any) {
        handleError({ error: error, message: error.message, level: "error" });
      }
    };
    const getProbeDetails = async () => {
      try {
        const organizationProbesResponse = await networkService.get<any>(
          `${ORGANIZATION_PROBES_API}/${user?.organizationId}`
        );
        const probeCount = organizationProbesResponse.data.results.length;
        setInitialValues((prev) => ({
          ...prev,
          nickName: `probe-${probeCount > 0 ? probeCount : 1}`,
        }));
      } catch (error: any) {
        handleError({ error: error, message: error.message, level: "error" });
      }
    };
    user?.role === UserRole.Member && getProbeDetails();
    fetchManufacturers().then((data) => {
      if (deviceId !== "new") {
        fetchDevice(data);
      } else {
        fetchLicense();
      }
    });
  }, [deviceId, orgId, licenseId, user?.organizationId, user?.role]);

  useEffect(() => {
    const fetchModelIds = async () => {
      setLoadingModels(true);
      try {
        const response = await networkService.get<any>(GET_MANUFACTURER);
        let iQ3Id = "";
        let iQPlusId = "";
        response.data.forEach((manufacturer: any) => {
          manufacturer.models.forEach((model: any) => {
            if (model.modelName.toLowerCase().includes("iq3")) iQ3Id = model.id;
            if (model.modelName.toLowerCase().includes("iq+")) iQPlusId = model.id;
          });
        });
        setModelIds({ iQ3: iQ3Id, iQPlus: iQPlusId });
      } catch (error) {
        handleError({ error, message: (error as Error).message, level: "error" });
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModelIds();
  }, []);

  const handleUpdate = async (values: {
    manufacturer: {
      name: string;
      id: string;
      models: {
        modelName: string;
        instructions: string;
        id: string;
      }[];
    };
    model: { id: string; modelName: string };
    externalDeviceId: string;
    nickName: string;
  }) => {
    console.log("values", values);
    if (
      !validateNullishObjectFields<{
        manufacturer: {
          name: string;
          id: string;
          models: {
            modelName: string;
            instructions: string;
            id: string;
          }[];
        };
        model: { id: string; modelName: string };
        externalDeviceId: string;
        nickName: string;
      }>(values)
    ) {
      toast.error("Please fill all the fields");
      return;
    }
    try {
      const updateManufacturerResponse = await networkService.put<any>(
        `${GET_MANUFACTURER}/models/${values.model.id}`,
        {
          manufacturerId: values.manufacturer.id,
        }
      );
      if (updateManufacturerResponse) {
        // toast.success("Manufacturer updated successfully");
        const probeUpdateResponse = await networkService.put<any>(
          `${PROBE_DETAILS_API}/${orgId}/${deviceId}`,
          {
            modelId: values.model.id,
            externalDeviceId: values.externalDeviceId,
            nickName: values.nickName,
          }
        );
        if (probeUpdateResponse) {
          toast.success("Probe linked successfully");
          navigate(`/licenses/${licenseId || initialValues.licenseId}`);
        }
      }
    } catch (error: any) {
      if (error.message === "Device already registered") {
        setSerialNumberError("This probe is already linked to an existing license. You must first remove this probe from the currently linked license.");
      }
    }
  };

  const handleCreateNewProbe = async (values: {
    model: { id: string; modelName: string };
    externalDeviceId: string;
    nickName: string;
  }) => {
    try {
      if (!licenseId) {
        toast.error("Please select a license to assign to this probe");
        return;
      }

      // Check if we have a lookup result
      if (lookupResult?.exists && lookupResult.deviceId) {
        // Attach existing probe to license
        const attachResponse = await networkService.put<any>(
          `/licenses/attach/device/${licenseId}`,
          {
            deviceId: lookupResult.deviceId,
          }
        );
        if (attachResponse) {
          toast.success("Existing probe attached successfully");
          navigate(`/licenses/${licenseId}`);
        }
      } else {
        // Register new probe
        const response = await networkService.post<any>(
          `${REGISTER_DEVICE}/${orgId}/register`,
          {
            nickName: values.nickName,
            externalDeviceId: values.externalDeviceId,
            modelId: values.model.id,
            isActive: true,
          }
        );
        toast.success("Probe Created Successfully");
        if (response) {
          const deviceId = response.data.id;
          const addLicenseResponse = await networkService.put<any>(
            `${UPDATE_DEVICE_ON_LICENSE}/${licenseId}`,
            {
              deviceId: deviceId,
            }
          );
          if (addLicenseResponse) {
            // toast.success("License assigned successfully");
            navigate(`/licenses/${licenseId}`);
          }
        }
      }
    } catch (error: any) {
      setSerialNumberError("This probe is already linked to an existing license. You must first remove this probe from the currently linked license.");
    }
  };

  const isAttachProbe = location.pathname.includes("new");
  const headerText = "Link a probe"

  if (loadingModels) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#807CD6]"></div>
      <p className="mt-4 text-gray-600">Loading probe models...</p>
    </div>;
  }

  const probeValidationSchema = Yup.object({
    serialNumber: Yup.string()
      .test("starts-with-letters", "", (value) =>
        /^[A-Za-z]{2}/.test(value)
      )
      .test("exact-length", "", (value) =>
        value ? /^[A-Za-z0-9]{10}$/.test(value) : true
      )
      .test("no-parentheses", "", (value) =>
        value ? /^[^(^)]*$/.test(value) : true
      )
      .test("letters-numbers", "Must contain both letters and numbers", (value) =>
        value ? /^(?=.*[A-Za-z])(?=.*\d)/.test(value) : true
      )
      .required("This field is required"),
    probeName: Yup.string().required("Probe name is required"),
  });

  return (
    <div
      className={`font-space-grotesk flex flex-col flex-grow px-12 py-10 overflow-hidden ${collapsed ? `${user?.role === UserRole.Admin ? "ml-20" : "md:ml-20"}` : `${user?.role === UserRole.Admin ? "ml-72" : "md:ml-72"}`} transition-all duration-300 pb-24`}>

      <nav className="text-sm mb-6 hidden md:block" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/licenses" className="underline">Licenses</Link>
            <span className="mx-2">{'>'}</span>
          </li>
          <li className="flex items-center">
            <Link to={`/licenses/${licenseId}`} className="underline">License Details</Link>
            <span className="mx-2">{'>'}</span>
          </li>
          <li className="flex items-center">
            <span className="">Link a probe</span>
          </li>
        </ol>
      </nav>

      <div className="relative">
        <div className="flex gap-4 items-center mb-3 justify-start lg:justify-start shadow-none lg:shadow-none">
          <button
            onClick={() => navigate(-1)}
            className="border-2 border-black-600 p-2 rounded-full lg:hidden z-50"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-[20px] lg:text-3xl font-bold font-space-grotesk text-center md:text-left ml-[3.4rem] md:ml-1 lg:ml-0">
            {headerText}
          </h1>
        </div>
        <div className="border-t lg:border-t-0 border-[var(--Stroke,#EFEFF4)] shadow-md lg:shadow-none absolute bottom-[-0.25rem] h-28 w-[1000px] left-[-55px] right-0"></div>
      </div>

      <div className="flex md:flex-row flex-col gap-3 h-full">
        <Formik
          className="md:w-1/2 w-full"
          enableReinitialize
          initialValues={{
            serialNumber: initialValues.externalDeviceId || "",
            probeName: initialValues.nickName || "",
          }}
          validationSchema={probeValidationSchema}
          onSubmit={async (values, actions) => {
            try {
              const detectedModel = getModelFromSerialNumber(values.serialNumber);

              // Allow registration regardless of serial number prefix
              // For unknown prefixes, use a default model ID or let the backend handle it
              let modelIdToUse = detectedModel.modelId;
              let modelNameToUse = detectedModel.modelName;

              // If no specific model is detected, use iQ3 as default
              if (!modelIdToUse) {
                modelIdToUse = modelIds.iQ3;
                modelNameToUse = "iQ3";
              }

              if (isAttachProbe) {
                await handleCreateNewProbe({
                  model: { id: modelIdToUse, modelName: modelNameToUse },
                  externalDeviceId: values.serialNumber,
                  nickName: values.probeName,
                });
              } else {
                const manufacturer = manufacturers.find((man) =>
                  man.models.some((model) => model.id === modelIdToUse)
                );
                const model = manufacturer?.models.find(
                  (model) => model.id === modelIdToUse
                );

                await handleUpdate({
                  manufacturer: manufacturer ? {
                    name: manufacturer.name,
                    id: manufacturer.id,
                    models: manufacturer.models,
                  } : {
                    name: "",
                    id: "",
                    models: [],
                  },
                  model: { id: modelIdToUse, modelName: modelNameToUse },
                  externalDeviceId: values.serialNumber,
                  nickName: values.probeName,
                });
              }
            } catch (error: any) {
              if (
                error?.response?.data?.message === "Device already registered" ||
                error?.message === "Device already registered"
              ) {
                setSerialNumberError(
                  "This probe is already linked to an existing license. You must first remove this probe from the currently linked license."
                );
              } else {
                setSerialNumberError("");
                // handleError({ error, message: error.message, level: "error" });
              }
            } finally {
              actions.setSubmitting(false);
              // Clear lookup result after submission
              setLookupResult(null);
            }
          }}
        >
          {({ isSubmitting, setFieldValue, values }) => {
            const detectedModel = values.serialNumber ? getModelFromSerialNumber(values.serialNumber) : null;
            const debouncedSerialNumber = useDebounce(values.serialNumber, 500); // 500ms debounce

            useEffect(() => {
              if (serialNumberError && values.serialNumber) {
                setSerialNumberError("");
              }
            }, [values.serialNumber]);

            // Trigger lookup when debounced serial number changes
            useEffect(() => {
              if (debouncedSerialNumber && debouncedSerialNumber.length === 10) {
                lookupSerialNumber(debouncedSerialNumber);
              } else {
                setLookupResult(null);
              }
            }, [debouncedSerialNumber, lookupSerialNumber]);

            return (
              <Form className="md:w-1/2 mt-4">
                <div className="mt-0">
                  <div className="sm:px-0 mt-0">
                    <h3 className="text-[18px] lg:w-[90%] w-full font-normal leading-6 text-Neutral-900 font-space-grotesk mt-4 md:mt-0">
                      Give us the details of your probe so we can link it to your license.
                    </h3>
                  </div>
                  <div className="flex flex-col gap-3 max-w-[500px] mt-6">
                    <div>
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="serialNumber"
                          className="block text-md font-bold leading-6 text-gray-900 font-space-grotesk pb-2">
                          Probe Serial Number
                        </label>
                        <button
                          className="font-space-grotesk underline lg:hidden pb-2"
                          onClick={handleFindItClick}
                          type="button"
                        >
                          Find it
                        </button>
                      </div>
                      {serialNumberError && (
                        <div className="bg-[#FEF2F2] border border-[#F87171] text-[#EF4444] px-4 py-3 rounded-md mb-4 font-medium flex">
                          <span className="mr-2 mt-[3px]">
                            <InfoIcon size={16} />
                          </span>
                          {serialNumberError}
                        </div>
                      )}
                      <GenericValidationInput
                        setFieldValue={setFieldValue}
                        onValidationChange={handleValidationChange}
                        onFindItClick={handleFindItClick}
                        name="serialNumber"
                        placeholder="ex: AB12458364"
                        validationRules={validationRules}
                        fieldClassName={serialNumberError ? "border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : ""}
                      />
                      {/* Display detected model */}
                      {/* {detectedModel && (
                        <p className={`text-xs mt-2 ${detectedModel.modelName === "Probe unknown" ? "text-amber-600" : "text-green-600"}`}>
                          Detected model: {detectedModel.modelName}
                        </p>
                      )} */}
                      {/* Display lookup status */}
                      {isLookingUp && (
                        <p className="text-xs mt-2 text-blue-600">
                          Checking if probe exists...
                        </p>
                      )}
                      {/* {lookupResult && !isLookingUp && (
                        <p className={`text-xs mt-2 ${lookupResult.exists ? "text-green-600" : "text-blue-600"}`}>
                          {lookupResult.exists
                            ? "✓ Probe found - will attach existing probe"
                            : "✓ Probe not found - will register new probe"}
                        </p>
                      )} */}
                    </div>

                    <div className="mt-3">
                      <label
                        htmlFor="probeName"
                        className="block text-md font-bold leading-6 text-gray-900 font-space-grotesk pb-2">
                        Probe Name
                      </label>
                      <Field
                        id="probeName"
                        name="probeName"
                        type="text"
                        placeholder="Probe 1"
                        className="font-space-grotesk px-4 py-3 w-full rounded-lg border-[1px] border-[#D0D0D6] placeholder-gray input-text focus:outline-none"
                      />
                      <ErrorMessage
                        name="probeName"
                        component="p"
                        className="text-red-500 text-xs mt-2"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-10 flex md:flex-row flex-col flex-wrap gap-4">
                  <div className="flex justify-center items-center lg:w-36 bg-angular-gradient rounded-full p-0.5">
                    <button
                      type="submit"
                      className="bg-rounded-full text-md font-semibold sm:text-base md:text-base font-space-grotesk w-full bg-white-a700 rounded-full p-1.5 py-2 hover:bg-light-angular-gradient"
                      disabled={isSubmitting}>
                      {isAttachProbe ? "Save" : "Update"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/licenses/${licenseId || initialValues.licenseId}`)}
                    className="hidden lg:block py-2 px-10 rounded-full border-2 border-[#E0E0E5] text-md sm:text-base md:text-base font-space-grotesk">
                    Cancel
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>

        <div className="hidden md:block w-1/2 mt-4 left-0 bg-[#F8F8FB] p-7 rounded-lg max-h-fit" >
          <h2 className="font-space-grotesk text-xl font-bold">Find your serial number</h2>
          <p className="mt-5 font-space-grotesk leading-tight mb-5">
            The serial number is marked on the probe. It contains 10 characters, starting with letters.
          </p>

          <div className="flex flex-col gap-4 items-center">
            <div className="flex gap-2 mb-4 bg-white-a700 rounded-full p-1 w-fit">
              <button
                type="button"
                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === "iQ3" ? "bg-[#AFAFE7] text-white shadow" : "bg-white text-gray-700"}`}
                onClick={() => setReferenceImageModel("iQ3")}
              >
                iQ3
              </button>
              <button
                type="button"
                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === "iQ+" ? "bg-[#AFAFE7] text-white shadow" : "bg-white-a700 text-gray-700"}`}
                onClick={() => setReferenceImageModel("iQ+")}
              >
                iQ+
              </button>
            </div>
            <div className="mt-2 w-full flex justify-center">
              {referenceImageModel === "iQ3" ? (
                <img
                  src="/iQ3-serial-number-location.png"
                  alt="iQ3 serial number location"
                  className="w-[400px] h-auto rounded-md"
                />
              ) : (
                <img
                  src="/iQ+-serial-number-location.png"
                  alt="iQ+ serial number location"
                  className="w-[400px] h-auto rounded-md"
                />
              )}
            </div>
          </div>
        </div >
      </div >
      {/* Find It Modal */}
      {showFindItModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black-900 bg-opacity-40">
          <div className="bg-white-a700 rounded-xl shadow-lg max-w-md w-full p-6 relative mx-5">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
              onClick={() => setShowFindItModal(false)}
              aria-label="Close"
              type="button"
            >
              <span className="text-2xl">&times;</span>
            </button>
            <h2 className="text-xl font-bold mb-2 text-center">Probe Serial Number</h2>
            <p className="text-gray-700 text-center mb-4">The serial number is marked on the probe. It contains 10 characters, starting with letters.</p>
            <div className="flex gap-2 mb-4 bg-white-a700 rounded-full p-1 w-fit border border-gray-300 mx-auto">
              <button
                type="button"
                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === "iQ3" ? "bg-[#AFAFE7] text-white shadow" : "bg-white text-gray-700"}`}
                onClick={() => setReferenceImageModel("iQ3")}
              >
                iQ3
              </button>
              <button
                type="button"
                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === "iQ+" ? "bg-[#AFAFE7] text-white shadow" : "bg-white-a700 text-gray-700"}`}
                onClick={() => setReferenceImageModel("iQ+")}
              >
                iQ+
              </button>
            </div>
            <div className="relative flex justify-center items-center bg-[#F5F5F7] h-[260px] w-full">
              <img
                src={referenceImageModel === "iQ3" ? iQ3Image : iQPlusImage}
                alt={`${referenceImageModel} serial number location`}
                className="h-auto rounded-md"
              />
              <button
                className="absolute bottom-2 right-2 bg-[#C4C6EE] rounded-full p-2 shadow-md"
                onClick={() => setShowFullScreen(true)}
                type="button"
                aria-label="Full screen"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-16h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" /></svg>
              </button>
            </div>
          </div>
          {/* Full Screen Modal for the image */}
          <FullScreenModal
            images={[referenceImageModel === "iQ3" ? iQ3Image : iQPlusImage]}
            isOpen={showFullScreen}
            onClose={() => setShowFullScreen(false)}
          />
        </div>
      )}
    </div >
  );
};

export default UpdateProbeDetails;